import { useTranslation } from "react-i18next";
import SvgIcon from "../../../../../parts/SvgIcon";
import { getNutritionTargetsForPlanDay } from "../../../../../utils/nutrition-helpers";

function LogNutritionCard({ nutritionSummary, nutritionPlans, dayKey, exercises }) {
  const { t } = useTranslation(["athlete-portal", "common"]);
  const logged = nutritionSummary?.nutrients || {};
  // getTargetData (nutrition-helpers.js) derives kcal from macros when the
  // plan has no stored `calories` — every caller (this card, Table legacy,
  // Nutrition Progress) shares that one fallback now, no per-caller override.
  const target = getNutritionTargetsForPlanDay(nutritionPlans, dayKey, exercises);

  const proteins = logged.proteins || 0;
  const carbs = logged.carbs || 0;
  const fats = logged.fats || 0;
  // Derive calories from macros, matching NutrientsChart.getNutrientsData:
  // round each macro's kcal (4/4/9 per g) first, then round the sum.
  const proteinsKcal = Math.round(proteins * 4);
  const carbsKcal = Math.round(carbs * 4);
  const fatsKcal = Math.round(fats * 9);
  const calories = Math.round(proteinsKcal + carbsKcal + fatsKcal);

  const hasLogged = !!(calories || proteins || carbs || fats);
  if (!hasLogged && !target) return null;

  const gramAbbr = t("common:units.gramAbbr");
  const kcalAbbr = t("common:units.kcalAbbr");

  const ROWS = [
    {
      key: "proteins",
      label: t("calendar.cards.nutrition.protein"),
      unit: gramAbbr,
      fill: "protein",
    },
    {
      key: "carbs",
      label: t("calendar.cards.nutrition.carbs"),
      unit: gramAbbr,
      fill: "carbs",
    },
    {
      key: "fats",
      label: t("calendar.cards.nutrition.fat"),
      unit: gramAbbr,
      fill: "fat",
    },
    {
      key: "calories",
      label: t("calendar.cards.nutrition.kcalLabel"),
      unit: "",
      fill: "cal",
    },
  ];

  return (
    <div className="log-card log-card--nutrition">
      <div className="log-card__header">
        <SvgIcon src="/img/new-icons/nutrition.svg" useCurrentColor={true} />
        {t("calendar.cards.nutrition.title")}
        {target && (
          <span className="ml-auto text-(--white-40) font-normal text-[0.65rem]">
            {target.calories} {kcalAbbr}
          </span>
        )}
      </div>
      <div className="log-card__body">
        {ROWS.map(({ key, label, unit, fill }) => {
          const value = { calories, proteins, carbs, fats }[key];
          const roundedValue = Math.round(value);
          const goal = target ? target[key] || 0 : 0;

          if (!target || !goal) {
            return (
              <div key={key} className="log-card__nutri-row">
                <span>{label}</span>
                <span className="tabular-nums">
                  {roundedValue}
                  {unit}
                </span>
              </div>
            );
          }

          const remaining = Math.round((goal - value) * 10) / 10;
          const pct = Math.max(0, Math.min(100, (value / goal) * 100));

          return (
            <div key={key} className="log-card__nutri-item">
              <div className="log-card__nutri-row">
                <span>{label}</span>
                <span
                  className={`log-card__nutri-left log-card__nutri-left--${fill}`}
                >
                  {remaining >= 0
                    ? t("calendar.cards.nutrition.eatenLeft", {
                        eaten: roundedValue,
                        amount: remaining,
                        unit,
                      })
                    : t("calendar.cards.nutrition.eatenOver", {
                        eaten: roundedValue,
                        amount: -remaining,
                        unit,
                      })}
                </span>
              </div>
              <div className="log-card__nutri-track">
                <div
                  className={`log-card__nutri-fill log-card__nutri-fill--${fill}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default LogNutritionCard;
